<template>
  <v-content>
    <v-container>
      <v-card>
        <v-card-title>
          <v-icon>assessment</v-icon> Reportes de Control
        </v-card-title>
        
        <v-card-text>
          <v-row>
            <!-- Selector de Puesto -->
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedPuesto"
                :items="activePuestos"
                item-text="name"
                item-value="id"
                label="Seleccionar Puesto"
                prepend-icon="business"
                return-object
              >
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="getEmails(item).length > 0">
                      {{ getEmails(item).join(', ') }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-select>
            </v-col>
            
            <!-- Selector de rango de días -->
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedDays"
                :items="dayOptions"
                label="Período"
                prepend-icon="date_range"
              ></v-select>
            </v-col>
            
            <!-- Selector de formato -->
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedFormat"
                :items="formatOptions"
                label="Formato"
                prepend-icon="save_alt"
              ></v-select>
            </v-col>
          </v-row>
          
          <!-- Info del puesto seleccionado -->
          <v-row v-if="selectedPuesto">
            <v-col cols="12">
              <v-alert type="info" text>
                <div class="d-flex align-center">
                  <div class="flex-grow-1">
                    <strong>{{ selectedPuesto.name }}</strong>
                    <div class="text-caption">
                      {{ getZonesCount(selectedPuesto.id) }} zonas asignadas
                    </div>
                    <div class="text-caption" v-if="getEmails(selectedPuesto).length > 0">
                      📧 {{ getEmails(selectedPuesto).join(', ') }}
                    </div>
                  </div>
                  <v-btn
                    color="primary"
                    @click="downloadReport"
                    :loading="downloading"
                    :disabled="!selectedPuesto"
                  >
                    <v-icon left>cloud_download</v-icon>
                    Descargar Reporte
                  </v-btn>
                </div>
              </v-alert>
            </v-col>
          </v-row>
          
          <!-- Opciones rápidas -->
          <v-row v-if="!selectedPuesto">
            <v-col cols="12">
              <v-alert type="warning" text>
                Selecciona un puesto para generar el reporte
              </v-alert>
            </v-col>
          </v-row>
          
          <!-- Accesos rápidos por puesto -->
          <v-row>
            <v-col cols="12">
              <v-subheader>Accesos Rápidos</v-subheader>
              <v-chip-group column>
                <v-chip
                  v-for="puesto in activePuestos"
                  :key="puesto.id"
                  @click="quickDownload(puesto)"
                  :color="selectedPuesto && selectedPuesto.id === puesto.id ? 'primary' : ''"
                >
                  <v-icon left small>business</v-icon>
                  {{ puesto.name }}
                </v-chip>
              </v-chip-group>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      
      <!-- Historial de descargas recientes (opcional) -->
      <v-card class="mt-4" v-if="downloadHistory.length > 0">
        <v-card-title>
          <v-icon>history</v-icon> Descargas Recientes
        </v-card-title>
        <v-list>
          <v-list-item
            v-for="(item, index) in downloadHistory"
            :key="index"
          >
            <v-list-item-avatar>
              <v-icon>{{ item.format === 'csv' ? 'table_chart' : 'description' }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ item.puesto }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ item.days === 1 ? 'Últimas 24 horas' : `Últimos ${item.days} días` }} - 
                {{ item.format.toUpperCase() }} - 
                {{ item.timestamp }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon @click="repeatDownload(item)">
                <v-icon>refresh</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>
      
      <v-snackbar v-model="snackbar" :color="snackbarColor">
        {{ snackbarText }}
        <v-btn text @click="snackbar = false">Cerrar</v-btn>
      </v-snackbar>
    </v-container>
  </v-content>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  name: "Reportes",
  data() {
    return {
      selectedPuesto: null,
      selectedDays: 1,
      selectedFormat: 'html',
      downloading: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      downloadHistory: [], // Historial de descargas en localStorage
      dayOptions: [
        { text: 'Últimas 24 horas', value: 1 },
        { text: 'Últimos 3 días', value: 3 },
        { text: 'Última semana', value: 7 },
        { text: 'Últimos 15 días', value: 15 },
        { text: 'Último mes', value: 30 }
      ],
      formatOptions: [
        { text: 'HTML (Vista web)', value: 'html' }
      ]
    }
  },
  
  mounted() {
    this.bindPuestos()
    this.bindZones()
    this.loadDownloadHistory()
  },
  
  methods: {
    ...mapActions({
      bindPuestos: 'puesto/bindPuestos',
      bindZones: 'zone/bindZones'
    }),
    
    getEmails(item) {
      if (item.emails && Array.isArray(item.emails)) {
        return item.emails
      }
      if (item.email && typeof item.email === 'string') {
        return [item.email]
      }
      return []
    },
    
    getZonesCount(puestoId) {
      return this.zones.filter(z => z.puestoId === puestoId).length
    },
    
downloadReport() {
  if (!this.selectedPuesto) {
    this.showSnackbar('Selecciona un puesto', 'error');
    return;
  }

  this.downloading = true;
  
  // Primero, encuentra tus URLs en Firebase Console
  // Ejecuta: firebase functions:list
  // O ve a Firebase Console → Functions
  
  const puestoId = this.selectedPuesto.id;
  const days = this.selectedDays;
  const format = this.selectedFormat;
  
  let url;
  if (format === 'csv') {
    // Reemplaza con tu URL real de downloadReportCSV
    url = `https://downloadreportcsv-agg7ofggba-uc.a.run.app/?puestoId=${puestoId}&days=${days}`;
  } else {
    // Reemplaza con tu URL real de downloadReport
    url = `https://downloadreport-agg7ofggba-uc.a.run.app/?puestoId=${puestoId}&days=${days}`;
  }
  
 
  window.open(url, '_blank');
  
  // Guardar en historial
  this.addToHistory(this.selectedPuesto.name, days, format);
  
  setTimeout(() => {
    this.downloading = false;
    this.showSnackbar('Descarga iniciada', 'success');
  }, 1000);
},
    
    quickDownload(puesto) {
      this.selectedPuesto = puesto
      this.downloadReport()
    },
    
    repeatDownload(item) {
      const puesto = this.activePuestos.find(p => p.name === item.puesto)
      if (puesto) {
        this.selectedPuesto = puesto
        this.selectedDays = item.days
        this.selectedFormat = item.format
        this.downloadReport()
      }
    },
    
    addToHistory(puesto, days, format) {
      const timestamp = new Date().toLocaleString('es-CO')
      const item = { puesto, days, format, timestamp }
      
      this.downloadHistory.unshift(item)
      
      // Mantener solo las últimas 10 descargas
      if (this.downloadHistory.length > 10) {
        this.downloadHistory.pop()
      }
      
      // Guardar en localStorage
      localStorage.setItem('reportDownloadHistory', JSON.stringify(this.downloadHistory))
    },
    
    loadDownloadHistory() {
      const saved = localStorage.getItem('reportDownloadHistory')
      if (saved) {
        try {
          this.downloadHistory = JSON.parse(saved)
        } catch (e) {
          this.downloadHistory = []
        }
      }
    },
    
    showSnackbar(text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  },
  
  computed: {
    ...mapState({
      puestos: state => state.puesto.puestos,
      zones: state => state.zone.zones || []
    }),
    ...mapGetters({
      activePuestos: 'puesto/activePuestos'
    })
  }
}
</script>

<style scoped>
</style>